import { create } from 'zustand';

const API_BASE_URL = '/api';

const useNewsBulletinsStore = create((set, get) => ({
  bulletins: [],
  calendarDates: [],
  searchResults: [],
  loading: false,
  error: null,
  hasMore: false,
  currentContextId: null,
  selectedDate: null,
  selectedBulletinId: null,

  // Fetch bulletins for a context
  fetchBulletins: async (contextId, offset = 0, limit = 10, token = null) => {
    set({ loading: true, error: null });
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Include date parameter if selectedDate is set (for paginated date-filtered bulletins)
      const state = get();
      const dateParam = state.selectedDate ? `&date=${state.selectedDate}` : '';

      const response = await fetch(
        `${API_BASE_URL}/news-bulletins?context_id=${contextId}&offset=${offset}&limit=${limit}${dateParam}`,
        { headers }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch bulletins');
      }

      if (data.success) {
        set((state) => ({
          bulletins: offset === 0 ? data.bulletins : [...state.bulletins, ...data.bulletins],
          hasMore: data.has_more,
          currentContextId: contextId,
          loading: false,
        }));
      } else {
        set({ error: data.message, loading: false });
      }
    } catch (error) {
      console.error('Error fetching bulletins:', error);
      set({ error: error.message, loading: false });
    }
  },

  // Fetch calendar data for a context
  fetchCalendarData: async (contextId, token = null) => {
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}/news-bulletins/calendar?context_id=${contextId}`,
        { headers }
      );

      const data = await response.json();

      if (data.success) {
        set({ calendarDates: data.dates });
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  },

  // Search bulletins
  searchBulletins: async (contextId, query, token = null) => {
    set({ loading: true, error: null });
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}/news-bulletins/search?context_id=${contextId}&query=${encodeURIComponent(query)}`,
        { headers }
      );

      const data = await response.json();

      if (data.success) {
        set({ searchResults: data.results, loading: false });
      } else {
        set({ error: data.message, loading: false });
      }
    } catch (error) {
      console.error('Error searching bulletins:', error);
      set({ error: error.message, loading: false });
    }
  },

  // Create a new bulletin
  createBulletin: async (contextId, bulletinData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/news-bulletins`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          context_id: contextId,
          ...bulletinData,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh bulletins
        await get().fetchBulletins(contextId, 0, 10, token);
        await get().fetchCalendarData(contextId, token);
        set({ loading: false });
        return { success: true, uuid: data.uuid };
      } else {
        set({ error: data.message, loading: false });
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error creating bulletin:', error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Update a bulletin
  updateBulletin: async (uuid, updateData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/news-bulletins/${uuid}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (data.success) {
        // Refetch bulletins to get updated gallery data
        const currentState = get();
        if (currentState.currentContextId) {
          await get().fetchBulletins(currentState.currentContextId, 0, 10, token);
        }
        set({ loading: false });
        return { success: true };
      } else {
        set({ error: data.message, loading: false });
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error updating bulletin:', error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Delete a bulletin
  deleteBulletin: async (uuid, contextId, token) => {
    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/news-bulletins/${uuid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        // Remove from local state
        set((state) => ({
          bulletins: state.bulletins.filter((bulletin) => bulletin.uuid !== uuid),
          loading: false,
        }));
        // Refresh calendar data
        await get().fetchCalendarData(contextId, token);
        return { success: true };
      } else {
        set({ error: data.message, loading: false });
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error deleting bulletin:', error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Toggle pin status
  togglePin: async (uuid, isPinned, contextId, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/news-bulletins/${uuid}/pin`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_pinned: isPinned }),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh bulletins to get updated pin order
        await get().fetchBulletins(contextId, 0, 10, token);
        return { success: true };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
      return { success: false, error: error.message };
    }
  },

  // Reorder pinned bulletins
  reorderBulletins: async (orderedUuids, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/news-bulletins/reorder`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: orderedUuids }),
      });

      const data = await response.json();

      if (data.success) {
        return { success: true };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error reordering bulletins:', error);
      return { success: false, error: error.message };
    }
  },

  // Jump to a specific date
  jumpToDate: async (contextId, date, token = null) => {
    set({ loading: true, error: null, selectedDate: date });
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Fetch bulletins from that date backward (using backend filtering)
      const response = await fetch(
        `${API_BASE_URL}/news-bulletins?context_id=${contextId}&offset=0&limit=10&date=${date}`,
        { headers }
      );

      const data = await response.json();

      if (data.success) {
        set({
          bulletins: data.bulletins,
          hasMore: data.has_more,
          loading: false,
          currentContextId: contextId,
        });
      } else {
        set({ error: data.message, loading: false });
      }
    } catch (error) {
      console.error('Error jumping to date:', error);
      set({ error: error.message, loading: false });
    }
  },

  // Reset to latest view
  resetToLatest: async (contextId, token = null) => {
    set({ selectedDate: null, selectedBulletinId: null });
    await get().fetchBulletins(contextId, 0, 10, token);
  },

  // Jump to a specific bulletin (from search results)
  jumpToBulletin: async (contextId, bulletinId, token = null) => {
    set({ loading: true, error: null, selectedBulletinId: bulletinId });
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Fetch bulletins from that bulletin backward
      const response = await fetch(
        `${API_BASE_URL}/news-bulletins?context_id=${contextId}&offset=0&limit=10&bulletin_id=${bulletinId}`,
        { headers }
      );

      const data = await response.json();

      if (data.success) {
        set({
          bulletins: data.bulletins,
          hasMore: data.has_more,
          loading: false,
          currentContextId: contextId,
        });
      } else {
        set({ error: data.message, loading: false });
      }
    } catch (error) {
      console.error('Error jumping to bulletin:', error);
      set({ error: error.message, loading: false });
    }
  },

  // Clear search results
  clearSearch: () => {
    set({ searchResults: [] });
  },

  // Reset store
  reset: () => {
    set({
      bulletins: [],
      calendarDates: [],
      searchResults: [],
      loading: false,
      error: null,
      hasMore: false,
      currentContextId: null,
      selectedDate: null,
      selectedBulletinId: null,
    });
  },
}));

export default useNewsBulletinsStore;
